import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Tooltip } from '../components/Tooltip';
import { NotificationPermissionBanner } from '../components/NotificationPermissionBanner';

function Welcome() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [installButtonLoading, setInstallButtonLoading] = useState(false);
  const [installCompleted, setInstallCompleted] = useState(false);

  useEffect(() => {
    // Check if app is installed/running in standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone || // iOS
                      document.referrer.includes('android-app://'); // Android
    setIsStandalone(standalone);

    // Check if installation was completed previously
    const wasInstalled = localStorage.getItem('ajel_app_installed');
    if (wasInstalled) {
      setInstallCompleted(true);
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    checkBiometricAvailability();

    // Listen for app installation completion
    const handleAppInstalled = () => {
      console.log('App installed successfully');
      setInstallCompleted(true);
      localStorage.setItem('ajel_app_installed', 'true');
      setDeferredPrompt(null);
      setInstallButtonLoading(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    const savedCredentials = localStorage.getItem('ajel_credentials');
    if (savedCredentials) {
      const { phone: savedPhone, password: savedPassword } = JSON.parse(savedCredentials);
      setPhone(savedPhone);
      setPassword(savedPassword);
      setRememberMe(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      if ('credentials' in navigator && window.PublicKeyCredential) {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricAvailable(available);
      } else {
        console.log('WebAuthn not supported on this device');
        setBiometricAvailable(false);
      }
    } catch (err) {
      console.error('Error checking biometric availability:', err);
      setBiometricAvailable(false);
    }
  };

  const handleInstall = async () => {
    setInstallButtonLoading(true);
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          console.log('User accepted installation');
          // Keep loading state until 'appinstalled' event fires
        } else {
          console.log('User dismissed installation');
          setInstallButtonLoading(false);
        }
      } catch (error) {
        console.error('Installation failed:', error);
        setInstallButtonLoading(false);
      }
    } else {
      setInstallButtonLoading(false);
    }
  };

  const handleOpenApp = () => {
    if (installCompleted && isStandalone) {
      // This will open the installed PWA
      window.location.href = window.location.origin;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log('Attempting login for phone:', phone);

      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email: `${phone}@ajel.life`,
        password,
      });

      if (signInError) {
        console.error('Sign in error:', signInError);
        throw signInError;
      }
      if (!authData.user) throw new Error('لم يتم العثور على المستخدم');

      console.log('Authentication successful, fetching profile...');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select()
        .eq('id', authData.user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Profile error:', profileError);
        throw profileError;
      }

      if (!profile) {
        await supabase.auth.signOut();
        setError('الحساب غير موجود. الرجاء التسجيل أولاً');
        return;
      }

      console.log('Profile found, login successful');

      if (rememberMe) {
        localStorage.setItem('ajel_credentials', JSON.stringify({ phone, password }));
      } else {
        localStorage.removeItem('ajel_credentials');
      }

      navigate('/home', { replace: true });
    } catch (err) {
      console.error('Sign in error:', err);
      
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          setError('فشل في الاتصال بالخادم. تحقق من اتصالك بالإنترنت وحاول مرة أخرى.');
        } else if (err.message.includes('Invalid login credentials')) {
          setError('رقم الهاتف أو كلمة المرور غير صحيحة');
        } else {
          setError(err.message);
        }
      } else {
        setError('حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show install screen if app is not installed
  if (!isStandalone) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-pink-50 flex flex-col">
        {/* Notification Permission Banner */}
        <NotificationPermissionBanner />
        
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8 relative overflow-hidden border border-purple-100">
            {/* Decorative elements */}
            <div className="absolute -top-16 -right-16 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-70"></div>
            <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-gradient-to-tr from-pink-100 to-orange-100 rounded-full opacity-70"></div>
            
            <div className="relative text-center">
              {/* App Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-4xl">{installCompleted ? '🎉' : '✨'}</span>
                </div>
              </div>
              
              {/* Instruction Text */}
              {installCompleted ? (
                <div className="mb-4">
                  <p className="text-xl font-bold text-green-600 mb-2">مبروك! 🎉</p>
                  <p className="text-lg font-medium text-gray-800 mb-3">تم تثبيت التطبيق على جهازك</p>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-12 h-12 rounded-xl overflow-hidden shadow-sm">
                        <img 
                          src="/icons/icon-192x192.png" 
                          alt="Ajel App Icon"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-green-800 font-medium">ابحث عن أيقونة "آجل" في تطبيقاتك</p>
                        <p className="text-green-700 text-sm">
                          ستجد التطبيق باسم "Ajel"
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-lg font-medium text-gray-800 mb-4">انقر زر تحميل التطبيق</p>
              )}
              
              {/* Download Button - First Thing */}
              {!installCompleted && (
                <button
                  onClick={handleInstall}
                  disabled={installButtonLoading || !deferredPrompt}
                  className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mb-8"
                >
                  <Smartphone className="w-6 h-6" />
                  <span className="font-medium text-lg">
                    {installButtonLoading ? 'جاري التثبيت...' : 'تحميل التطبيق'}
                  </span>
                </button>
              )}
              
              {/* App Name and Description */}
              <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">آجل</h1>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">دفتر حسابات اجل</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                تطبيق لإدارة الحسابات والديون بطريقة سهلة وآمنة. احصل على تجربة أفضل مع التطبيق المثبت على جهازك.
              </p>
              
              {/* Features List */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-8">
                <h3 className="font-semibold text-gray-800 mb-3">مميزات التطبيق:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <span>إدارة الحسابات والديون بسهولة</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    <span>إشعارات فورية للمعاملات الجديدة</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-pink-500 rounded-full"></span>
                    <span>طباعة كشوف الحساب المفصلة</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <span>واجهة سهلة ومريحة للعينين</span>
                  </li>
                </ul>
              </div>
              
              {!deferredPrompt && !installCompleted && (
                <div className="w-full p-4 bg-gray-100 rounded-xl mb-4">
                  <p className="text-gray-600 text-sm text-center">
                    التطبيق متاح للتثبيت من متصفحك. ابحث عن خيار "إضافة إلى الشاشة الرئيسية" أو "تثبيت التطبيق" في قائمة المتصفح.
                  </p>
                </div>
              )}
              
              {/* Skip Installation Button */}
              {!installCompleted && (
                <button
                  onClick={() => setIsStandalone(true)}
                  className="w-full text-gray-600 hover:text-gray-800 text-sm underline"
                >
                  متابعة في المتصفح
                </button>
              )}
              
              {/* New option for completed installation */}
              {installCompleted && (
                <button
                  onClick={() => setIsStandalone(true)}
                  className="w-full text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  متابعة في المتصفح (إذا لم تجد التطبيق)
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show login form if app is installed/standalone
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-pink-50 flex flex-col">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />
      
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8 relative overflow-hidden border border-purple-100">
          {/* Decorative elements */}
          <div className="absolute -top-16 -right-16 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-70"></div>
          <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-gradient-to-tr from-pink-100 to-orange-100 rounded-full opacity-70"></div>
          
          <div className="relative text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">✨</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">آجل</h1>
            <div className="flex items-center justify-center space-x-2">
              <p className="text-gray-600">دفتر حسابات اجل</p>
              <Tooltip text="تطبيق لإدارة الحسابات والديون بطريقة سهلة وآمنة" />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleLogin}>
            <div className="relative flex items-center">
              <input
                type="tel"
                placeholder="أدخل رقم الهاتف"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-left pl-12"
                required
              />
              <div className="absolute left-4">
                <Tooltip text="أدخل رقم الهاتف المسجل في حسابك" position="right" />
              </div>
            </div>

            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="كلمة المرور"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-left pl-12 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4"
              >
                {showPassword ? <EyeOff size={20} className="text-gray-400" /> : <Eye size={20} className="text-gray-400" />}
              </button>
              <div className="absolute left-4">
                <Tooltip text="أدخل كلمة المرور الخاصة بحسابك" position="right" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">تذكرني</span>
              </label>
              <button 
                type="button"
                onClick={() => navigate('/reset-password')}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                استعادة كلمة المرور
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
            </button>
          </form>

          <div className="mt-8">
            <p className="mt-8 text-center text-sm text-gray-500">
              ليس لديك حساب؟{' '}
              <button 
                onClick={() => navigate('/register')} 
                className="font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-80"
              >
                سجل الآن
              </button>
            </p>
          
            {/* Celebratory bottom element */}
            <div className="mt-8 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-purple-100 shadow-sm">
              <p className="text-center text-sm text-blue-800">
                انضم إلى أكثر من 100,000+ مستخدم يديرون أعمالهم بسهولة مع آجل 🚀
              </p>
            </div>
          </div>

          {!isStandalone && deferredPrompt && (
            <div className="mt-6">
              <button
                onClick={handleInstall}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
              >
                <Smartphone className="w-5 h-5" />
                <span>تثبيت التطبيق</span>
              </button>
              <p className="mt-2 text-xs text-center text-gray-500">احصل على تجربة أفضل مع تطبيق آجل</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Welcome;